import { useEffect, useState } from "react";
import { Pencil, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { GENDER, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Confirm } from "@/components/Common";
import { PageTitle } from "./AdminLayout";
import { LeadDialog, LeadPill } from "./LeadDialog";

export function useParticipants() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/participants", { params: { q } }).then((r) => setItems(r.data.items));
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  return { q, setQ, items, load };
}

export const SearchBox = ({ q, setQ }) => (
  <div className="relative w-full sm:w-72">
    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
    <Input className="bg-white pl-9" placeholder="Cari nama / telepon / email / kota" value={q} onChange={(e) => setQ(e.target.value)} data-testid="participant-search-input" />
  </div>
);

function ParticipantEditor({ participant, onOpenChange, onDone }) {
  const [v, setV] = useState(null);
  useEffect(() => {
    setV(participant ? { ...participant, pin: "" } : null);
  }, [participant]);
  if (!v) return null;
  const save = async () => {
    try {
      const payload = {
        full_name: v.full_name,
        display_name: v.display_name || v.full_name,
        dob: v.dob,
        gender: v.gender,
        city: v.city,
        emergency_phone: v.emergency_phone,
      };
      if (!v.is_child) {
        payload.phone = v.phone;
        if (v.pin) payload.pin = v.pin;
      }
      await api.put(`/admin/participants/${v.id}`, payload);
      toast.success("Data peserta diperbarui.");
      onDone();
      onOpenChange(false);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  return (
    <Dialog open={!!participant} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="participant-editor-dialog">
        <DialogHeader><DialogTitle>Edit peserta</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Nama lengkap</Label><Input value={v.full_name} onChange={(e) => setV({ ...v, full_name: e.target.value })} data-testid="admin-participant-name-input" /></div>
          <div><Label>Nama tampilan</Label><Input value={v.display_name || ""} onChange={(e) => setV({ ...v, display_name: e.target.value })} data-testid="admin-participant-display-input" /></div>
          <div><Label>Tanggal lahir</Label><Input type="date" value={v.dob} onChange={(e) => setV({ ...v, dob: e.target.value })} data-testid="admin-participant-dob-input" /></div>
          <div><Label>Jenis kelamin</Label><Select value={v.gender} onValueChange={(gender) => setV({ ...v, gender })}><SelectTrigger data-testid="admin-participant-gender-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="L">Laki-laki</SelectItem><SelectItem value="P">Perempuan</SelectItem></SelectContent></Select></div>
          <div><Label>Kota domisili</Label><Input value={v.city} onChange={(e) => setV({ ...v, city: e.target.value })} data-testid="admin-participant-city-input" /></div>
          <div className="sm:col-span-2"><Label>Telepon darurat</Label><Input value={v.emergency_phone} onChange={(e) => setV({ ...v, emergency_phone: e.target.value })} data-testid="admin-participant-emergency-input" /></div>
          {!v.is_child && <><div><Label>Nomor WhatsApp</Label><Input value={v.phone || ""} onChange={(e) => setV({ ...v, phone: e.target.value })} data-testid="admin-participant-phone-input" /></div><div><Label>PIN baru (opsional)</Label><Input type="password" inputMode="numeric" maxLength={6} value={v.pin} onChange={(e) => setV({ ...v, pin: e.target.value })} data-testid="admin-participant-pin-input" /></div></>}
        </div>
        {v.is_child && <p className="text-xs text-muted-foreground">PIN dan nomor login milik akun wali.</p>}
        <Button onClick={save} data-testid="admin-participant-save-button">Simpan perubahan</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Participants() {
  const { q, setQ, items, load } = useParticipants();
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(null);
  const remove = async (p) => {
    try {
      await api.delete(`/admin/participants/${p.id}`);
      toast.success(p.is_child ? "Profil anak dihapus." : "Akun peserta dihapus.");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  return (
    <div>
      <PageTitle title="Peserta" desc={`${items.length} profil. Data kontak darurat hanya untuk admin.`}><SearchBox q={q} setQ={setQ} /></PageTitle>
      <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-[#1A3323]/10">
        <Table data-testid="participants-table">
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Telepon</TableHead><TableHead>Umur / Gender</TableHead><TableHead>Kota</TableHead><TableHead>Darurat</TableHead><TableHead>Hadir</TableHead><TableHead>Climb</TableHead><TableHead>Lead</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`participant-row-${p.id}`}>
                <TableCell className="font-semibold text-[#1A3323]">{p.full_name}{p.is_child && <div className="text-xs font-normal text-[#3D6F4B]">Anak · wali {p.guardian?.name} ({p.guardian?.phone})</div>}<div className="text-xs font-normal text-muted-foreground">daftar {fmtDate(p.created_at)}</div></TableCell>
                <TableCell>{p.phone || "-"}{p.email && <div className="text-xs text-muted-foreground" data-testid={`participant-email-${p.id}`}>{p.email}</div>}</TableCell>
                <TableCell>{p.age} th · {GENDER[p.gender]}</TableCell>
                <TableCell>{p.city}</TableCell>
                <TableCell className="text-xs">{p.emergency_phone}</TableCell>
                <TableCell className="font-mono">{p.attendance}</TableCell>
                <TableCell className="font-mono">{p.completed}</TableCell>
                <TableCell><LeadPill s={p.lead_status} /></TableCell>
                <TableCell><div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => setLead(p)} data-testid={`lead-manage-${p.id}`}>Lead</Button><Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-participant-${p.id}`}><Pencil className="h-4 w-4" /></Button><Confirm testid={`delete-participant-${p.id}`} title={`Hapus ${p.full_name}?`} desc={p.is_child ? "Profil anak beserta riwayat climbing, tiket, dan check-in akan dihapus permanen." : "Akun beserta semua profil (termasuk anak), riwayat climbing, tiket, check-in, dan poin leaderboard akan dihapus permanen."} destructive confirmText="Hapus permanen" onConfirm={() => remove(p)} trigger={<Button size="icon" variant="ghost" data-testid={`delete-participant-${p.id}-button`}><Trash2 className="h-4 w-4 text-red-600" /></Button>} /></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <LeadDialog p={lead} onOpenChange={(o) => !o && setLead(null)} onDone={load} />
      <ParticipantEditor participant={editing} onOpenChange={(open) => !open && setEditing(null)} onDone={load} />
    </div>
  );
}
